/**
 * Search, filter, sort, and facet the public tournament list, and keep the
 * result shareable via the URL. Coaches search the way they talk — "12u",
 * "dallas", "summer slam", "florida", or all four at once — so a query
 * matches on name, location, state, division, and venue, and every term
 * must land somewhere. Filters narrow by state, division, registration
 * status, and timeframe; facet helpers drive the dropdown options with
 * counts; and the filter/sort state round-trips through the query string
 * so a filtered view survives a refresh or a shared link.
 */
import { stateName } from "@alpha/shared";
import { registrationOpen } from "./format";
import type { TournamentListItem } from "./types";

/** Split a query into lowercase terms. Empty query -> no terms -> matches all. */
export function queryTerms(query: string): string[] {
  return query.toLowerCase().split(/\s+/).filter(Boolean);
}

function haystack(t: TournamentListItem): string {
  return [
    t.name,
    t.location,
    t.state ?? "",
    stateName(t.state),
    ...t.summary.divisions,
    ...t.summary.venues.map((v) => v.name),
  ]
    .join(" ")
    .toLowerCase();
}

export function matchesTerms(t: TournamentListItem, terms: string[]): boolean {
  if (terms.length === 0) return true;
  const hay = haystack(t);
  return terms.every((term) => hay.includes(term));
}

/**
 * Did this division name cause the match? Drives the highlighted chip on a
 * card, so a coach who typed "12u" can see which division answered.
 */
export function divisionMatches(division: string, terms: string[]): boolean {
  if (terms.length === 0) return false;
  const name = division.toLowerCase();
  return terms.some((term) => name.includes(term));
}

export type RegistrationFilter = "all" | "open";
export type TimeframeFilter = "upcoming" | "past" | "all";

export interface TournamentFilters {
  /** Free text over name, location, state, division, and venue. */
  query: string;
  /** A US state code, or "all". */
  state: string;
  /** A division name, or "all". */
  division: string;
  registration: RegistrationFilter;
  timeframe: TimeframeFilter;
}

/** Coaches care about what's ahead, so the list opens on upcoming events. */
export const DEFAULT_FILTERS: TournamentFilters = {
  query: "",
  state: "all",
  division: "all",
  registration: "all",
  timeframe: "upcoming",
};

/** Today as a local date-only string, so "upcoming" matches the viewer's calendar. */
export function todayIso(now: Date = new Date()): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

/**
 * An event counts as upcoming through its final day. Dates are compared as
 * YYYY-MM-DD strings, where lexicographic order is chronological order.
 */
function isUpcoming(t: TournamentListItem, today: string): boolean {
  return t.endDate.slice(0, 10) >= today;
}

export function applyFilters(
  tournaments: TournamentListItem[],
  filters: TournamentFilters,
  today: string = todayIso(),
): TournamentListItem[] {
  const terms = queryTerms(filters.query);
  return tournaments.filter((t) => {
    if (!matchesTerms(t, terms)) return false;
    if (filters.state !== "all" && t.state !== filters.state) return false;
    if (filters.division !== "all" && !t.summary.divisions.includes(filters.division)) return false;
    if (filters.registration === "open" && !registrationOpen(t)) return false;
    if (filters.timeframe === "upcoming" && !isUpcoming(t, today)) return false;
    if (filters.timeframe === "past" && isUpcoming(t, today)) return false;
    return true;
  });
}

export type SortOrder = "date" | "name";
export const DEFAULT_SORT: SortOrder = "date";

/** Returns a new sorted array; ties break on id so the order never wobbles. */
export function sortTournaments(
  tournaments: TournamentListItem[],
  sort: SortOrder,
): TournamentListItem[] {
  const out = [...tournaments];
  out.sort((a, b) => {
    const primary =
      sort === "name"
        ? a.name.localeCompare(b.name, undefined, { sensitivity: "base" })
        : a.startDate.localeCompare(b.startDate);
    return primary !== 0 ? primary : a.id.localeCompare(b.id);
  });
  return out;
}

/** One selectable value in a filter dropdown, with how many events it matches. */
export interface FacetOption {
  value: string;
  label: string;
  count: number;
}

function tally(values: string[]): Map<string, number> {
  const counts = new Map<string, number>();
  for (const v of values) counts.set(v, (counts.get(v) ?? 0) + 1);
  return counts;
}

/** States actually present in the list, so the picker never offers a dead end. */
export function availableStates(tournaments: TournamentListItem[]): FacetOption[] {
  const counts = tally(tournaments.map((t) => t.state ?? "").filter(Boolean));
  return [...counts.entries()]
    .map(([value, count]) => ({ value, label: stateName(value), count }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

/** Divisions across the list, ordered so 9U sorts before 10U. */
export function availableDivisions(tournaments: TournamentListItem[]): FacetOption[] {
  const counts = tally(tournaments.flatMap((t) => t.summary.divisions));
  return [...counts.entries()]
    .map(([value, count]) => ({ value, label: value, count }))
    .sort((a, b) => a.label.localeCompare(b.label, undefined, { numeric: true }));
}

/** Query-string keys. Short, because these end up in shared links. */
const PARAM = {
  query: "q",
  state: "state",
  division: "division",
  registration: "reg",
  timeframe: "when",
  sort: "sort",
} as const;

export function readFilters(params: URLSearchParams): TournamentFilters {
  const registration = params.get(PARAM.registration);
  const timeframe = params.get(PARAM.timeframe);
  return {
    query: params.get(PARAM.query) ?? DEFAULT_FILTERS.query,
    state: params.get(PARAM.state) ?? DEFAULT_FILTERS.state,
    division: params.get(PARAM.division) ?? DEFAULT_FILTERS.division,
    registration: registration === "open" ? "open" : "all",
    timeframe: timeframe === "past" || timeframe === "all" ? timeframe : "upcoming",
  };
}

export function readSort(params: URLSearchParams): SortOrder {
  return params.get(PARAM.sort) === "name" ? "name" : DEFAULT_SORT;
}

/** Only non-default values are written, so an unfiltered page has a bare URL. */
export function writeParams(filters: TournamentFilters, sort: SortOrder): URLSearchParams {
  const params = new URLSearchParams();
  // Write the query verbatim. The search box is controlled from this URL, so
  // trimming here would delete a trailing space as fast as it is typed and make
  // multi-word queries ("12u dallas") impossible. `queryTerms` splits on
  // whitespace anyway, so untrimmed text costs nothing at match time.
  if (filters.query.trim()) params.set(PARAM.query, filters.query);
  if (filters.state !== DEFAULT_FILTERS.state) params.set(PARAM.state, filters.state);
  if (filters.division !== DEFAULT_FILTERS.division) params.set(PARAM.division, filters.division);
  if (filters.registration !== DEFAULT_FILTERS.registration)
    params.set(PARAM.registration, filters.registration);
  if (filters.timeframe !== DEFAULT_FILTERS.timeframe) params.set(PARAM.timeframe, filters.timeframe);
  if (sort !== DEFAULT_SORT) params.set(PARAM.sort, sort);
  return params;
}

/** Is anything narrowing the list right now? Drives the "Clear filters" button. */
export function isFiltered(filters: TournamentFilters): boolean {
  return (
    filters.query.trim() !== "" ||
    filters.state !== DEFAULT_FILTERS.state ||
    filters.division !== DEFAULT_FILTERS.division ||
    filters.registration !== DEFAULT_FILTERS.registration ||
    filters.timeframe !== DEFAULT_FILTERS.timeframe
  );
}
